import React, { useEffect, useState } from "react";
import { Download, X, Share2, PlusSquare } from "lucide-react";

/**
 * Discrete "Install MyGlobalTopup" banner.
 *
 * Two paths depending on the platform:
 *   • Android / Chrome desktop / Edge — captures the `beforeinstallprompt`
 *     event and exposes an "Installer" button that fires the native
 *     install sheet.
 *   • iOS Safari — no programmatic install exists, so we show a short
 *     "Partager → Sur l'écran d'accueil" tutorial with the two Safari
 *     icons (share arrow + '+' box) so users can install manually.
 *
 * Dismissed choice is remembered in localStorage for 7 days so the
 * banner doesn't nag on every page navigation.
 */

const DISMISS_KEY = "mgt.install_prompt_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  // iOS Safari sets `navigator.standalone`; every other browser follows
  // the display-mode media query.
  if (typeof window === "undefined") return false;
  const iosStandalone = "standalone" in window.navigator && window.navigator.standalone;
  const mqStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return Boolean(iosStandalone || mqStandalone);
}

function detectIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // iPhone / iPad (iPadOS 13+ reports MacIntel with touch)
  const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
    (ua.includes("Macintosh") && "ontouchend" in document);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS && isSafari;
}

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null); // "android" | "ios" | null

  useEffect(() => {
    // Already installed → nothing to do.
    if (isStandalone()) return;

    // Recently dismissed → respect the user's choice.
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (at && Date.now() - at < DISMISS_TTL_MS) return;
    } catch {
      /* ignore private-mode storage errors */
    }

    // iOS Safari — no programmatic prompt. Show the tutorial banner
    // after a small delay so the user sees the login page first.
    if (detectIOS()) {
      setPlatform("ios");
      const timer = setTimeout(() => setVisible(true), 4000);
      return () => clearTimeout(timer);
    }

    // Android / Chrome / Edge — wait for the beforeinstallprompt event.
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform("android");
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    // Also react to "appinstalled" so we hide the banner immediately once
    // the user completes the flow.
    const installedHandler = () => setVisible(false);
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* user dismissed — treat as decline */
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-50"
      data-testid="install-pwa-banner"
    >
      <div className="rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <img
            src="/icon-192.png"
            alt=""
            className="w-11 h-11 rounded-lg flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-slate-900">
              Installer MyGlobalTopup
            </div>
            {platform === "ios" ? (
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                Ouvrez le menu{" "}
                <Share2 className="w-3.5 h-3.5 inline mx-0.5 text-[#0F62FE]" strokeWidth={2.4} />
                {" "}puis choisissez{" "}
                <span className="inline-flex items-center gap-0.5 font-semibold text-slate-800">
                  «&nbsp;Sur l&apos;écran d&apos;accueil&nbsp;»{" "}
                  <PlusSquare className="w-3.5 h-3.5 inline text-[#0F62FE]" strokeWidth={2.4} />
                </span>
                .
              </div>
            ) : (
              <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                Un accès rapide depuis votre écran d&apos;accueil, en plein écran, comme une vraie application.
              </div>
            )}
          </div>
          <button
            onClick={dismiss}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Fermer"
            data-testid="install-pwa-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {platform === "android" && (
          <div className="flex border-t border-slate-100">
            <button
              onClick={dismiss}
              className="flex-1 text-xs font-semibold text-slate-600 py-2.5 hover:bg-slate-50"
              data-testid="install-pwa-later"
            >
              Plus tard
            </button>
            <button
              onClick={install}
              className="flex-1 text-xs font-bold text-white bg-[#0F62FE] hover:bg-[#0043CE] py-2.5 inline-flex items-center justify-center gap-1.5"
              data-testid="install-pwa-install"
            >
              <Download className="w-3.5 h-3.5" />
              Installer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
